import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Key, UserX, UserCheck, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SearchInput from "@/components/SearchInput";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
const empty = { email: "", name: "", phone: "", address: "", tin: "", company: "",
                customer_type: "walk_in", credit_line_enabled: false, credit_limit: 0,
                active: true, password: "", notes: "" };

const CustomersPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setItems(data);
    } catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/customers");
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) toast.error("Failed to load customers");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c, e) => {
    e?.stopPropagation();
    setEditing(c); setForm({ ...empty, ...c, password: "" }); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, credit_limit: parseFloat(form.credit_limit) || 0 };
      if (editing) {
        const { password, email, ...rest } = payload;
        const body = { ...rest };
        if (password) body.password = password;
        await api.patch(`/admin/customers/${editing.user_id}`, body);
        toast.success("Customer updated");
      } else {
        await api.post("/admin/customers", payload);
        toast.success("Customer created");
      }
      setOpen(false);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (c, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try { await api.delete(`/admin/customers/${c.user_id}`); toast.success("Deleted"); fetchAll(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (c, e) => {
    e?.stopPropagation();
    try {
      await api.patch(`/admin/customers/${c.user_id}`, { active: c.active === false });
      toast.success(c.active === false ? "Enabled" : "Disabled");
      fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const resetPassword = async (c, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Reset password for ${c.name}?`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.user_id}/reset-password`);
      window.prompt("Temporary password (copy and share securely):", data.temp_password);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Customers</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Customers</h1>
          <p className="text-sm text-neutral-500 mt-1">Manage online and walk-in customers, credit lines, and statements of account.</p>
        </div>
        <button onClick={openCreate} className="btn-accent inline-flex items-center gap-2" data-testid="customer-create-button">
          <Plus className="h-4 w-4" /> Add customer
        </button>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        <div className="p-4 border-b border-[#E2E8F0] flex items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            onEnter={fetchAll}
            placeholder="Search name, email, phone, company…"
            data-testid="customer-search-input"
            className="flex-1" />
          <button onClick={fetchAll} className="text-sm text-[#0B1E3F] font-semibold hover:underline whitespace-nowrap" data-testid="customer-search-button">Search</button>
        </div>
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <p className="p-10 text-center text-neutral-500">No customers found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-center px-4 py-3">Credit Line</th>
                  <th className="text-right px-4 py-3">Outstanding</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0] text-sm">
                {items.map((c) => (
                  <tr key={c.user_id} onClick={() => navigate(`/admin/customers/${c.user_id}`)}
                    data-testid={`customer-row-${c.user_id}`}
                    className="hover:bg-[#F8FAFC] cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold">{c.name}</p>
                      {c.company && <p className="text-xs text-neutral-500">{c.company}</p>}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{c.email}</td>
                    <td className="px-4 py-3 text-neutral-700">{c.phone || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="pav-chip capitalize">{(c.customer_type || "walk_in").replace("_", " ")}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input type="checkbox" checked={c.credit_line_enabled === true}
                        onChange={(e) => { e.stopPropagation();
                          api.patch(`/admin/customers/${c.user_id}`, { credit_line_enabled: e.target.checked })
                            .then(() => fetchAll())
                            .catch((er) => toast.error(er?.response?.data?.detail || "Failed")); }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`customer-credit-${c.user_id}`} />
                    </td>
                    <td className="px-4 py-3 text-right font-display font-bold text-[#0B1E3F]">
                      {c.credit_outstanding > 0 ? peso(c.credit_outstanding) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`pav-chip ${c.active === false ? "status-cancelled" : "status-completed"}`}>
                        {c.active === false ? "disabled" : "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/admin/chat-inbox?customer=${c.user_id}`); }}
                        data-testid={`customer-chat-${c.user_id}`}
                        className="p-1.5 hover:bg-cyan-50 rounded" title="Chat with customer">
                        <MessageCircle className="h-4 w-4 text-[#06B6D4]" />
                      </button>
                      <button onClick={(e) => openEdit(c, e)} data-testid={`customer-edit-${c.user_id}`}
                        className="p-1.5 hover:bg-[#F4F6FB] rounded" title="Edit">
                        <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                      </button>
                      <button onClick={(e) => resetPassword(c, e)} data-testid={`customer-reset-${c.user_id}`}
                        className="p-1.5 hover:bg-[#F4F6FB] rounded" title="Reset password">
                        <Key className="h-4 w-4 text-[#0B1E3F]" />
                      </button>
                      <button onClick={(e) => toggleActive(c, e)} data-testid={`customer-toggle-${c.user_id}`}
                        className="p-1.5 hover:bg-yellow-50 rounded" title={c.active === false ? "Enable" : "Disable"}>
                        {c.active === false ? <UserCheck className="h-4 w-4 text-green-700" /> : <UserX className="h-4 w-4 text-yellow-700" />}
                      </button>
                      <button onClick={(e) => remove(c, e)} data-testid={`customer-delete-${c.user_id}`}
                        className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit customer" : "Add customer"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Full name</label>
                <input className="pav-input mt-1" data-testid="customer-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Email</label>
                <input type="email" className="pav-input mt-1" disabled={!!editing}
                  data-testid="customer-email-input"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Phone</label>
                <input className="pav-input mt-1" data-testid="customer-phone-input"
                  value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Company</label>
                <input className="pav-input mt-1" data-testid="customer-company-input"
                  value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Address</label>
                <input className="pav-input mt-1" data-testid="customer-address-input"
                  value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">TIN</label>
                <input className="pav-input mt-1" data-testid="customer-tin-input"
                  value={form.tin || ""} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Customer type</label>
                <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                  <SelectTrigger className="pav-input mt-1" data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walk_in">Walk-In</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 bg-[#F4F6FB] rounded-lg p-3">
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input type="checkbox" data-testid="customer-credit-checkbox"
                    checked={form.credit_line_enabled}
                    onChange={(e) => setForm({ ...form, credit_line_enabled: e.target.checked })} />
                  Allow credit line (On Account payments)
                </label>
                {form.credit_line_enabled && (
                  <div className="mt-2">
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Credit limit (₱, 0 = no limit)</label>
                    <input type="number" min="0" step="100" className="pav-input mt-1"
                      data-testid="customer-credit-limit-input"
                      value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">{editing ? "New password (blank = unchanged)" : "Password (optional)"}</label>
                <input type="password" className="pav-input mt-1" data-testid="customer-password-input"
                  value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="customer-active-checkbox"
                  checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Account is active
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="customer-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
